import React, {
    useState, useCallback, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
    Box, Dialog, DialogContent, Typography, Button,
    LinearProgress, Paper, Chip, IconButton, Checkbox,
} from '@material-ui/core';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DescriptionIcon from '@material-ui/icons/Description';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import ButtonRound from 'components/controls/ButtonRound';
import { abreviaturas } from 'constantes';
import PdfRenderer from './PdfRenderer';
import SignaturePad from './SignaturePad';
import Camera from './Camera';
import {
    addSignatureToPdf,
    pdfBytesToBlob,
    blobToArrayBuffer,
    base64ToArrayBuffer,
    downloadPdf,
    downloadMultiplePdfsAsZip,
    resolveDropPosition,
    clampToPage,
} from '../../utils/pdfUtils';

const useStyles = makeStyles(() => ({
    container: {
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    header: {
        backgroundColor: '#FFFFFF',
        color: '#53546A',
        padding: '0.75rem 1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #E0E0E0',
    },
    headerTitle: {
        fontFamily: 'Montserrat',
        fontWeight: 'bold',
        fontSize: '1rem',
        color: '#15325C',
    },
    headerProgress: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    progressText: {
        fontFamily: 'Montserrat',
        fontSize: '0.875rem',
        color: '#53546A',
    },
    mainContent: {
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
    },
    sidebar: {
        width: '250px',
        backgroundColor: '#F5F8FF',
        borderRight: '1px solid #E0E0E0',
        overflowY: 'auto',
        padding: '1rem',
    },
    sidebarTitle: {
        fontFamily: 'Montserrat',
        fontWeight: 'bold',
        fontSize: '0.875rem',
        color: '#15325C',
        marginBottom: '0.75rem',
    },
    documentItem: {
        display: 'flex',
        alignItems: 'center',
        padding: '0.75rem',
        marginBottom: '0.5rem',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        transition: 'all 0.2s',
        border: '2px solid transparent',
        '&:hover': {
            backgroundColor: '#E8ECFF',
        },
    },
    documentItemActive: {
        backgroundColor: '#E8ECFF',
        borderColor: '#C00333',
    },
    documentItemSigned: {
        backgroundColor: '#E8F5E9',
    },
    documentItemFailed: {
        backgroundColor: '#FFEBEE',
    },
    documentItemSkipped: {
        backgroundColor: '#FFF3E0',
    },
    documentIcon: {
        marginRight: '0.5rem',
        color: '#666666',
    },
    documentIconSigned: {
        color: '#4CAF50',
    },
    documentIconFailed: {
        color: '#F44336',
    },
    documentIconSkipped: {
        color: '#FF9800',
    },
    documentName: {
        fontFamily: 'Montserrat',
        fontSize: '0.75rem',
        color: '#333333',
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    pdfContainer: {
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
    },
    navigationBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.25rem 1rem',
        backgroundColor: '#FFFFFF',
        borderTop: '1px solid #E0E0E0',
        minHeight: '40px',
    },
    navButton: {
        fontFamily: 'Montserrat',
        fontSize: '0.875rem',
        textTransform: 'capitalize',
        fontWeight: '600',
        color: '#15325C',
    },
    signatureOverlay: {
        position: 'absolute',
        border: '2px solid #C00333',
        backgroundColor: 'rgba(192, 3, 51, 0.1)',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 50,
    },
    signatureContainer: {
        position: 'absolute',
        zIndex: 50,
    },
    signatureActions: {
        position: 'absolute',
        top: -20,
        right: -16,
        display: 'flex',
        gap: 2,
        pointerEvents: 'auto',
    },
    actionButton: {
        backgroundColor: '#C00333',
        color: '#fff',
        width: 24,
        height: 24,
        padding: 2,
        '&:hover': {
            backgroundColor: '#900025',
        },
    },
    signaturePreview: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
    },
    dialogContent: {
        padding: 0,
    },
    selfiePreview: {
        position: 'fixed',
        bottom: 80,
        left: 265,
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        border: '3px solid #C00333',
        objectFit: 'cover',
        zIndex: 1000,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    },
    applyAllButton: {
        marginTop: '1rem',
    },
    applyAllButtonText: {
        fontFamily: 'Montserrat',
        fontSize: '0.875rem',
        color: '#C00333',
        backgroundColor: '#F5F8FF',
        paddingTop: '0.625rem',
        paddingBottom: '0.625rem',
        borderRadius: '0.625rem',
        boxShadow: '0rem 0.125rem 0.25rem #ED2D5D33',
        textTransform: 'capitalize',
        fontWeight: '600',
    },
    processingOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
    },
    processingCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        padding: '2rem',
        textAlign: 'center',
        maxWidth: '400px',
    },
    processingTitle: {
        fontFamily: 'Montserrat',
        fontWeight: 'bold',
        fontSize: '1.25rem',
        color: '#15325C',
        marginBottom: '1rem',
    },
    processingText: {
        fontFamily: 'Montserrat',
        fontSize: '0.875rem',
        color: '#666666',
    },
}));

function BatchPdfViewer({ pdfFiles, onHandleComplete }) {
    const classes = useStyles();

    // Estado de documentos
    // status: pending, positioned, processing, saved (enviado OK), failed (error servidor)
    const [documents, setDocuments] = useState(
        pdfFiles.map((file, index) => ({
            id: `doc-${index}`,
            name: file.name || `Documento ${index + 1}`,
            file,
            signatures: [], // Array de { id, position }
            status: 'pending',
            signedBlob: null,
        })),
    );

    const [currentDocIndex, setCurrentDocIndex] = useState(0);
    // IDs de documentos seleccionados para firmar
    const [selectedDocIds, setSelectedDocIds] = useState([]);
    const [signatureDataUrl, setSignatureDataUrl] = useState(null);
    const [selfieDataUrl, setSelfieDataUrl] = useState(null);
    const [showCamera, setShowCamera] = useState(false);
    const [showSignaturePad, setShowSignaturePad] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingProgress, setProcessingProgress] = useState(0);
    const [isSigningComplete, setIsSigningComplete] = useState(false);
    const [currentPdfScale, setCurrentPdfScale] = useState(1.5);

    // Ref para posicion pendiente mientras se captura la primera firma
    const pendingRef = useRef(null); // { position, docIndex }

    // Drag: mover firma individual con mouse/touch
    const isDragging = useRef(false);
    const draggingId = useRef(null);
    const draggingDocId = useRef(null);
    const dragOffset = useRef({ x: 0, y: 0 });
    const scaleRef = useRef(currentPdfScale);
    scaleRef.current = currentPdfScale;

    // Callback cuando cambia el zoom en PdfRenderer
    const handleScaleChange = useCallback((newScale) => {
        setCurrentPdfScale(newScale);
    }, []);

    // Reinicializar estado cuando pdfFiles cambia
    useEffect(() => {
        setDocuments(
            pdfFiles.map((file, index) => ({
                id: `doc-${index}`,
                name: file.name || `Documento ${index + 1}`,
                file,
                signatures: [],
                status: 'pending',
                signedBlob: null,
            })),
        );
        setCurrentDocIndex(0);
        setSelectedDocIds([]);
        setSignatureDataUrl(null);
        setSelfieDataUrl(null);
        setIsSigningComplete(false);
        setProcessingProgress(0);
    }, [pdfFiles]);

    const currentDoc = documents[currentDocIndex];
    const completedDocs = documents.filter((d) => d.signatures.length > 0).length;
    const totalDocs = documents.length;

    // Click en PDF: si ya hay firma, agrega una nueva; si no, inicia captura
    const handlePageClick = useCallback((position) => {
        const newPos = clampToPage({
            x: position.x - 75,
            y: position.y - 25,
            pageIndex: position.pageIndex,
            canvasHeight: position.canvasHeight,
            width: 150,
            height: 50,
            scale: position.scale,
        }, position.canvasWidth / position.scale, position.canvasHeight / position.scale);

        if (signatureDataUrl) {
            // Ya hay firma capturada: agregar nueva al documento actual
            setDocuments((prev) => prev.map((doc, idx) => (idx === currentDocIndex
                ? {
                    ...doc,
                    signatures: [...doc.signatures, { id: Date.now(), position: newPos }],
                    status: 'positioned',
                }
                : doc)));
        } else {
            // Sin firma aun: guardar posicion pendiente y mostrar camara
            pendingRef.current = { position: newPos, docIndex: currentDocIndex };
            setShowCamera(true);
        }
    }, [currentDocIndex, signatureDataUrl, documents]);

    // Manejar selfie capturada
    const handleSelfieCapture = useCallback((dataUrl) => {
        setSelfieDataUrl(dataUrl);
        setShowCamera(false);
        setShowSignaturePad(true);
    }, []);

    // Omitir selfie
    const handleSkipSelfie = useCallback(() => {
        setShowCamera(false);
        setShowSignaturePad(true);
    }, []);

    // Firma confirmada: guarda dataUrl y crea la primera firma en el doc pendiente
    const handleSignatureConfirm = useCallback((dataUrl) => {
        setSignatureDataUrl(dataUrl);
        setShowSignaturePad(false);
        if (pendingRef.current) {
            const { position, docIndex } = pendingRef.current;
            setDocuments((prev) => prev.map((doc, idx) => (idx === docIndex
                ? {
                    ...doc,
                    signatures: [{ id: Date.now(), position }],
                    status: 'positioned',
                }
                : doc)));
            pendingRef.current = null;
        }
    }, []);

    // Navegacion entre documentos
    const handlePrevious = useCallback(() => {
        if (currentDocIndex > 0) {
            setCurrentDocIndex(currentDocIndex - 1);
        }
    }, [currentDocIndex]);

    const handleNext = useCallback(() => {
        if (currentDocIndex < documents.length - 1) {
            setCurrentDocIndex(currentDocIndex + 1);
        }
    }, [currentDocIndex, documents.length]);

    const handleGoToDocument = useCallback((index) => {
        setCurrentDocIndex(index);
    }, []);

    // Eliminar firma individual por id.
    // Si era la ultima firma de todos los docs, resetea para volver a capturar.
    const handleDeleteSignature = useCallback((docId, sigId) => {
        setDocuments((prev) => {
            const updated = prev.map((doc) => {
                if (doc.id !== docId) return doc;
                const remaining = doc.signatures.filter((s) => s.id !== sigId);
                return {
                    ...doc,
                    signatures: remaining,
                    status: remaining.length === 0 ? 'pending' : 'positioned',
                };
            });
            const anySignaturesLeft = updated.some((d) => d.signatures.length > 0);
            if (!anySignaturesLeft) {
                setSignatureDataUrl(null);
                setSelfieDataUrl(null);
            }
            return updated;
        });
    }, []);

    // Crecer firma individual
    const handleGrowSignature = useCallback((docId, sigId) => {
        setDocuments((prev) => prev.map((doc) => {
            if (doc.id !== docId) return doc;
            return {
                ...doc,
                signatures: doc.signatures.map((s) => {
                    if (s.id !== sigId) return s;
                    return {
                        ...s,
                        position: {
                            ...s.position,
                            width: s.position.width + 30,
                            height: s.position.height + 10,
                        },
                    };
                }),
            };
        }));
    }, []);

    // Reducir firma individual
    const handleShrinkSignature = useCallback((docId, sigId) => {
        setDocuments((prev) => prev.map((doc) => {
            if (doc.id !== docId) return doc;
            return {
                ...doc,
                signatures: doc.signatures.map((s) => {
                    if (s.id !== sigId) return s;
                    return {
                        ...s,
                        position: {
                            ...s.position,
                            width: Math.max(s.position.width - 30, 60),
                            height: Math.max(s.position.height - 10, 20),
                        },
                    };
                }),
            };
        }));
    }, []);

    // Inicio de drag para firma individual
    const handleDragStart = useCallback((e, sigId) => {
        e.preventDefault();
        e.stopPropagation();
        isDragging.current = true;
        draggingId.current = sigId;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        setDocuments((prev) => {
            const doc = prev[currentDocIndex];
            draggingDocId.current = doc.id;
            const sig = doc.signatures.find((s) => s.id === sigId);
            if (sig) {
                const { position: pos } = sig;
                const overlayLeft = pos.x * currentPdfScale;
                const pdfPageH = pos.canvasHeight / pos.scale;
                const overlayTop = pdfPageH * currentPdfScale
                    - (pos.y + pos.height) * currentPdfScale;
                dragOffset.current = {
                    x: clientX - overlayLeft,
                    y: clientY - overlayTop,
                };
            }
            return prev;
        });
    }, [currentDocIndex, currentPdfScale]);

    useEffect(() => {
        const handleDragMove = (e) => {
            if (!isDragging.current) return;
            e.preventDefault();
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            const newPixelX = clientX - dragOffset.current.x;
            const newPixelY = clientY - dragOffset.current.y;

            setDocuments((prev) => prev.map((doc) => {
                if (doc.id !== draggingDocId.current) return doc;
                return {
                    ...doc,
                    signatures: doc.signatures.map((s) => {
                        if (s.id !== draggingId.current) return s;
                        const sc = scaleRef.current;
                        const pdfPageH = s.position.canvasHeight / s.position.scale;
                        return {
                            ...s,
                            position: {
                                ...s.position,
                                x: newPixelX / sc,
                                y: pdfPageH - (newPixelY / sc) - s.position.height,
                            },
                        };
                    }),
                };
            }));
        };

        const handleDragEnd = () => {
            if (!isDragging.current) return;
            const sigId = draggingId.current;
            const docId = draggingDocId.current;
            isDragging.current = false;
            draggingId.current = null;
            draggingDocId.current = null;

            // Si se arrastro a otra pagina, se ancla a esa pagina
            setDocuments((prev) => prev.map((doc) => {
                if (doc.id !== docId) return doc;
                return {
                    ...doc,
                    signatures: doc.signatures.map((s) => (s.id === sigId
                        ? { ...s, position: resolveDropPosition(s.position, scaleRef.current) }
                        : s)),
                };
            }));
        };

        window.addEventListener('mousemove', handleDragMove);
        window.addEventListener('mouseup', handleDragEnd);
        window.addEventListener('touchmove', handleDragMove, { passive: false });
        window.addEventListener('touchend', handleDragEnd);

        return () => {
            window.removeEventListener('mousemove', handleDragMove);
            window.removeEventListener('mouseup', handleDragEnd);
            window.removeEventListener('touchmove', handleDragMove);
            window.removeEventListener('touchend', handleDragEnd);
        };
    }, []);

    // Aplicar firmas del doc actual a todos los demas documentos
    const handleApplyToAll = useCallback(() => {
        const docWithSignature = documents.find((d) => d.signatures.length > 0);
        if (docWithSignature) {
            setDocuments((prev) => prev.map((doc) => ({
                ...doc,
                signatures: docWithSignature.signatures,
                status: 'positioned',
            })));
        }
    }, [documents]);

    // Seleccionar / deseleccionar un documento para firmar
    const handleToggleSelect = useCallback((docId) => {
        setSelectedDocIds((prev) => (prev.includes(docId)
            ? prev.filter((id) => id !== docId)
            : [...prev, docId]));
    }, []);

    // Quitar firmas de todos los documentos y resetear captura
    const handleClearAllSignatures = useCallback(() => {
        setDocuments((prev) => prev.map((doc) => ({
            ...doc,
            signatures: [],
            status: 'pending',
            signedBlob: null,
        })));
        setSignatureDataUrl(null);
        setSelfieDataUrl(null);
        setSelectedDocIds([]);
    }, []);

    // Firmar todos los documentos y enviar al servidor
    const handleSignDocs = async (docsToSign) => {
        if (!signatureDataUrl || docsToSign.length === 0) {
            return;
        }

        setIsProcessing(true);
        setProcessingProgress(0);

        try {
            const signedDocuments = [];

            // Fase 1: Firmar localmente todos los documentos
            for (let i = 0; i < docsToSign.length; i += 1) {
                const doc = docsToSign[i];

                let pdfBytes;
                if (doc.file instanceof Blob) {
                    // eslint-disable-next-line no-await-in-loop
                    pdfBytes = await blobToArrayBuffer(doc.file);
                } else if (typeof doc.file === 'string') {
                    pdfBytes = base64ToArrayBuffer(doc.file);
                }

                // Agregar todas las firmas del documento al PDF
                // eslint-disable-next-line no-await-in-loop
                const signedPdfBytes = await addSignatureToPdf(
                    pdfBytes,
                    signatureDataUrl,
                    doc.signatures.map((s) => s.position),
                );

                const signedBlob = pdfBytesToBlob(signedPdfBytes);
                signedDocuments.push({
                    ...doc,
                    signedBlob,
                    status: 'processing',
                });

                setProcessingProgress(((i + 1) / docsToSign.length) * 50);
            }

            // Actualizar estado: documentos firmados localmente
            setDocuments((prev) => prev.map((doc) => {
                const signed = signedDocuments.find((s) => s.id === doc.id);
                return signed || doc;
            }));

            if (selfieDataUrl) {
                localStorage.setItem('lastSignatureSelfie', selfieDataUrl);
            }

            // Fase 2: Enviar al servidor
            const serverResults = await onHandleComplete(
                signedDocuments.map((d) => ({
                    id: d.id,
                    blob: d.signedBlob,
                    name: d.name,
                })),
                selfieDataUrl,
            );

            // Actualizar estado basado en resultados del servidor
            setDocuments((prev) => prev.map((doc, index) => {
                const signedDoc = signedDocuments.find((s) => s.id === doc.id);
                if (!signedDoc) return doc;

                let success = false;
                if (Array.isArray(serverResults)) {
                    const result = serverResults.find((r) => r.id === doc.id);
                    if (result) {
                        success = result.success;
                    } else if (typeof serverResults[index] === 'boolean') {
                        success = serverResults[index];
                    }
                }

                return {
                    ...signedDoc,
                    status: success ? 'saved' : 'failed',
                };
            }));

            // Marcar documentos sin firmas como 'skipped'
            setDocuments((prev) => prev.map((doc) => {
                if (doc.signatures.length === 0 && doc.status === 'pending') {
                    return { ...doc, status: 'skipped' };
                }
                return doc;
            }));

            setProcessingProgress(100);
            setIsSigningComplete(true);
        } catch (error) {
            setDocuments((prev) => prev.map((doc) => {
                if (doc.status === 'processing') {
                    return { ...doc, status: 'failed' };
                }
                return doc;
            }));
            setIsSigningComplete(true);
        } finally {
            setIsProcessing(false);
        }
    };

    // Firmar todos los documentos con firma posicionada
    const handleSignAll = async () => {
        const docsToSign = documents.filter((d) => d.signatures.length > 0);
        await handleSignDocs(docsToSign);
    };

    // Aplicar firma visualmente solo a los documentos seleccionados
    const handleApplyToSelected = useCallback(() => {
        const docWithSignature = documents.find((d) => d.signatures.length > 0);
        if (docWithSignature) {
            setDocuments((prev) => prev.map((doc) => {
                if (!selectedDocIds.includes(doc.id)) return doc;
                return {
                    ...doc,
                    signatures: docWithSignature.signatures,
                    status: 'positioned',
                };
            }));
            setSelectedDocIds([]);
        }
    }, [documents, selectedDocIds]);

    // Descargar un documento individual
    const handleDownloadSingle = useCallback((doc) => {
        if (doc.signedBlob) {
            const fileName = doc.name.replace('.pdf', '') || 'documento';
            downloadPdf(doc.signedBlob, `${fileName}_firmado`);
        }
    }, []);

    // Descargar todos los documentos guardados en ZIP
    const handleDownloadAllZip = useCallback(async () => {
        const docsToDownload = documents.filter((d) => d.status === 'saved' && d.signedBlob);
        if (docsToDownload.length === 0) return;

        const docsForZip = docsToDownload.map((doc) => ({
            blob: doc.signedBlob,
            name: `${doc.name.replace('.pdf', '')}_firmado`,
        }));

        await downloadMultiplePdfsAsZip(docsForZip, 'documentos_firmados');
    }, [documents]);

    // Construir overlays para PdfRenderer con todas las firmas del doc actual
    const signatureOverlays = signatureDataUrl
        ? currentDoc.signatures.map((sig) => ({
            pageIndex: sig.position.pageIndex,
            element: (
              <Box
                key={sig.id}
                className={classes.signatureContainer}
                style={{
                    left: sig.position.x * currentPdfScale,
                    top: (sig.position.canvasHeight / sig.position.scale)
                        * currentPdfScale
                        - (sig.position.y + sig.position.height)
                        * currentPdfScale,
                }}
              >
                <Box
                  className={classes.signatureOverlay}
                  style={{
                      position: 'relative',
                      width: sig.position.width * currentPdfScale,
                      height: sig.position.height * currentPdfScale,
                      cursor: 'grab',
                      pointerEvents: 'auto',
                  }}
                  onMouseDown={(e) => handleDragStart(e, sig.id)}
                  onTouchStart={(e) => handleDragStart(e, sig.id)}
                  role="presentation"
                >
                  <img
                    src={signatureDataUrl}
                    alt="Firma"
                    className={classes.signaturePreview}
                    draggable={false}
                  />
                </Box>
                <Box className={classes.signatureActions}>
                  <IconButton
                    className={classes.actionButton}
                    onClick={() => handleShrinkSignature(currentDoc.id, sig.id)}
                    size="small"
                  >
                    <RemoveIcon style={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton
                    className={classes.actionButton}
                    onClick={() => handleGrowSignature(currentDoc.id, sig.id)}
                    size="small"
                  >
                    <AddIcon style={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton
                    className={classes.actionButton}
                    onClick={() => handleDeleteSignature(currentDoc.id, sig.id)}
                    size="small"
                  >
                    <DeleteIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Box>
            ),
        }))
        : [];

    const showHint = !signatureDataUrl && currentDoc.signatures.length === 0;
    const savedDocs = documents.filter((d) => d.status === 'saved');
    const failedDocs = documents.filter((d) => d.status === 'failed');
    const skippedDocs = documents.filter((d) => d.status === 'skipped');

    return (
      <Box className={classes.container}>
        {/* Header con progreso */}
        <Box className={classes.header}>
          <Typography className={classes.headerTitle}>
            Firma Masiva de Documentos
          </Typography>
          <Box className={classes.headerProgress}>
            <Typography className={classes.progressText}>
              {completedDocs}
              {' '}
              /
              {' '}
              {totalDocs}
              {' '}
              posicionados
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(completedDocs / totalDocs) * 100}
              style={{ width: '100px', marginLeft: '0.5rem' }}
            />
          </Box>
        </Box>

        <Box className={classes.mainContent}>
          {/* Sidebar con lista de documentos */}
          <Box className={classes.sidebar}>
            <Typography className={classes.sidebarTitle}>
              Documentos (
              {totalDocs}
              )
            </Typography>

            {documents.map((doc, index) => {
                let statusClass = '';
                if (doc.status === 'saved') statusClass = classes.documentItemSigned;
                if (doc.status === 'failed') statusClass = classes.documentItemFailed;
                if (doc.status === 'skipped') statusClass = classes.documentItemSkipped;

                let IconComponent = DescriptionIcon;
                let iconClass = classes.documentIcon;
                if (doc.status === 'positioned' || doc.status === 'processing') {
                    IconComponent = CheckCircleIcon;
                    iconClass = `${classes.documentIcon} ${classes.documentIconSigned}`;
                } else if (doc.status === 'saved') {
                    IconComponent = CheckCircleIcon;
                    iconClass = `${classes.documentIcon} ${classes.documentIconSigned}`;
                } else if (doc.status === 'failed') {
                    IconComponent = ErrorOutlineIcon;
                    iconClass = `${classes.documentIcon} ${classes.documentIconFailed}`;
                } else if (doc.status === 'skipped') {
                    IconComponent = ErrorOutlineIcon;
                    iconClass = `${classes.documentIcon} ${classes.documentIconSkipped}`;
                }

                return (
                  <Box
                    key={doc.id}
                    className={`${classes.documentItem} ${
                        index === currentDocIndex ? classes.documentItemActive : ''
                    } ${statusClass}`}
                    onClick={() => handleGoToDocument(index)}
                  >
                    {/* Checkbox: visible para todos los docs cuando hay firma capturada */}
                    {signatureDataUrl && !isSigningComplete && (
                      <Checkbox
                        checked={selectedDocIds.includes(doc.id)}
                        onChange={() => handleToggleSelect(doc.id)}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                        style={{ padding: 2, color: '#C00333', marginRight: 4 }}
                      />
                    )}
                    <IconComponent className={iconClass} fontSize="small" />
                    <Typography className={classes.documentName}>
                      {doc.name}
                    </Typography>
                    {doc.status === 'failed' && (
                      <Chip label="Error" size="small" style={{ backgroundColor: '#F44336', color: '#FFF' }} />
                    )}
                    {doc.status === 'saved' && (
                      <Chip label="OK" size="small" style={{ backgroundColor: '#4CAF50', color: '#FFF' }} />
                    )}
                    {doc.status === 'skipped' && (
                      <Chip label="Omitido" size="small" style={{ backgroundColor: '#FF9800', color: '#FFF' }} />
                    )}
                    {index === currentDocIndex && doc.status !== 'saved' && doc.status !== 'failed' && doc.status !== 'skipped' && (
                      <Chip label="Actual" size="small" color="secondary" />
                    )}
                  </Box>
                );
            })}

            {/* Boton firmar seleccionados */}
            {selectedDocIds.length > 0 && signatureDataUrl && !isSigningComplete && (
              <Box className={classes.applyAllButton}>
                <Button
                  fullWidth
                  className={classes.applyAllButtonText}
                  onClick={handleApplyToSelected}
                >
                  {`Firmar (${selectedDocIds.length})`}
                </Button>
              </Box>
            )}

            {/* Boton aplicar a todos */}
            {completedDocs > 0 && signatureDataUrl && !isSigningComplete && (
              <Box className={classes.applyAllButton}>
                <Button
                  fullWidth
                  className={classes.applyAllButtonText}
                  onClick={handleApplyToAll}
                >
                  Firmar todos
                </Button>
              </Box>
            )}

            {/* Boton quitar todas las firmas */}
            {!isSigningComplete && completedDocs > 0 && (
              <Box style={{ marginTop: '0.5rem' }}>
                <Button
                  fullWidth
                  onClick={handleClearAllSignatures}
                  style={{
                      fontFamily: 'Montserrat',
                      fontSize: '0.75rem',
                      color: '#F44336',
                      backgroundColor: '#F5F8FF',
                      paddingTop: '0.5rem',
                      paddingBottom: '0.5rem',
                      borderRadius: '0.625rem',
                      textTransform: 'capitalize',
                      fontWeight: '600',
                      border: '1px solid #F44336',
                  }}
                >
                  Quitar todas las firmas
                </Button>
              </Box>
            )}

            {/* Botones de descarga */}
            {isSigningComplete && savedDocs.length > 0 && (
              <Box style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #E0E0E0' }}>
                <Typography
                  style={{
                      fontFamily: 'Montserrat',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      color: '#4CAF50',
                      marginBottom: '0.5rem',
                  }}
                >
                  Guardados Correctamente (
                  {savedDocs.length}
                  )
                </Typography>
                {savedDocs.length > 1 && (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleDownloadAllZip}
                    style={{
                        backgroundColor: '#4CAF50',
                        color: '#FFFFFF',
                        fontFamily: 'Montserrat',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        textTransform: 'capitalize',
                        marginBottom: '0.5rem',
                        paddingTop: '0.625rem',
                        paddingBottom: '0.625rem',
                        borderRadius: '0.625rem',
                        boxShadow: '0.125rem 0.25rem 0.625rem #ED2D5D33',
                    }}
                  >
                    Descargar Todo (ZIP)
                  </Button>
                )}
                {savedDocs.map((doc) => (
                  <Button
                    key={doc.id}
                    fullWidth
                    variant="outlined"
                    onClick={() => handleDownloadSingle(doc)}
                    style={{
                        fontFamily: 'Montserrat',
                        fontSize: '0.625rem',
                        fontWeight: '600',
                        textTransform: 'capitalize',
                        marginBottom: '0.25rem',
                        justifyContent: 'flex-start',
                        borderColor: '#4CAF50',
                        color: '#4CAF50',
                        paddingTop: '0.5rem',
                        paddingBottom: '0.5rem',
                        borderRadius: '0.625rem',
                        boxShadow: '0rem 0.125rem 0.25rem #4CAF5033',
                    }}
                  >
                    {doc.name}
                  </Button>
                ))}
              </Box>
            )}

            {/* Documentos fallidos */}
            {isSigningComplete && failedDocs.length > 0 && (
              <Box style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #E0E0E0' }}>
                <Typography
                  style={{
                      fontFamily: 'Montserrat',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      color: '#F44336',
                      marginBottom: '0.5rem',
                  }}
                >
                  Error al Guardar (
                  {failedDocs.length}
                  )
                </Typography>
                {failedDocs.map((doc) => (
                  <Typography
                    key={doc.id}
                    style={{
                        fontFamily: 'Montserrat',
                        fontSize: '0.625rem',
                        color: '#F44336',
                        marginBottom: '0.25rem',
                    }}
                  >
                    {`• ${doc.name}`}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Documentos omitidos */}
            {isSigningComplete && skippedDocs.length > 0 && (
              <Box style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #E0E0E0' }}>
                <Typography
                  style={{
                      fontFamily: 'Montserrat',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      color: '#FF9800',
                      marginBottom: '0.5rem',
                  }}
                >
                  Sin Firma Posicionada (
                  {skippedDocs.length}
                  )
                </Typography>
                {skippedDocs.map((doc) => (
                  <Typography
                    key={doc.id}
                    style={{
                        fontFamily: 'Montserrat',
                        fontSize: '0.625rem',
                        color: '#FF9800',
                        marginBottom: '0.25rem',
                    }}
                  >
                    {`• ${doc.name}`}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>

          {/* Area del PDF */}
          <Box className={classes.pdfContainer}>
            <PdfRenderer
              pdfFile={currentDoc.file}
              onPageClick={handlePageClick}
              showHint={showHint}
              onScaleChange={handleScaleChange}
              signatureOverlays={signatureOverlays}
            />
          </Box>
        </Box>

        {/* Preview de selfie */}
        {selfieDataUrl && (
          <img
            src={selfieDataUrl}
            alt="Selfie"
            className={classes.selfiePreview}
          />
        )}

        {/* Barra de navegacion */}
        <Box className={classes.navigationBar}>
          <Button
            startIcon={<NavigateBeforeIcon />}
            onClick={handlePrevious}
            disabled={currentDocIndex === 0}
            className={classes.navButton}
          >
            Anterior
          </Button>

          <Typography style={{ fontFamily: 'Montserrat' }}>
            Documento
            {' '}
            {currentDocIndex + 1}
            {' '}
            de
            {' '}
            {totalDocs}
          </Typography>

          <Box style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {currentDocIndex < documents.length - 1 && (
              <Button
                endIcon={<NavigateNextIcon />}
                onClick={handleNext}
                className={classes.navButton}
                color="primary"
              >
                Siguiente
              </Button>
            )}
            {completedDocs > 0 && signatureDataUrl && !isSigningComplete && (
              <Box
                style={{
                    transform: 'scale(0.75)',
                    transformOrigin: 'center right',
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                }}
              >
                <ButtonRound
                  caption="Enviar firmas"
                  colorBase={abreviaturas.DEFAULT_RED_COLOR}
                  onClickEvent={handleSignAll}
                  disabled={!signatureDataUrl || completedDocs === 0}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* Modal de camara */}
        <Dialog open={showCamera} onClose={() => setShowCamera(false)} maxWidth="sm" fullWidth>
          <DialogContent className={classes.dialogContent}>
            <Camera onCapture={handleSelfieCapture} onSkip={handleSkipSelfie} />
          </DialogContent>
        </Dialog>

        {/* Modal de firma */}
        <Dialog open={showSignaturePad} onClose={() => setShowSignaturePad(false)} maxWidth="sm" fullWidth>
          <DialogContent className={classes.dialogContent}>
            <SignaturePad
              onSignatureConfirm={handleSignatureConfirm}
              onCancel={() => setShowSignaturePad(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Overlay de procesamiento */}
        {isProcessing && (
          <Box className={classes.processingOverlay}>
            <Paper className={classes.processingCard}>
              <Typography className={classes.processingTitle}>
                Firmando documentos...
              </Typography>
              <LinearProgress
                variant="determinate"
                value={processingProgress}
                style={{ marginBottom: '1rem' }}
              />
              <Typography className={classes.processingText}>
                Procesando
                {' '}
                {Math.round(processingProgress)}
                % (
                {Math.round((processingProgress / 100) * totalDocs)}
                {' '}
                de
                {' '}
                {totalDocs}
                )
              </Typography>
            </Paper>
          </Box>
        )}
      </Box>
    );
}

BatchPdfViewer.propTypes = {
    pdfFiles: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    ).isRequired,
    onHandleComplete: PropTypes.func.isRequired,
};

export default BatchPdfViewer;
